import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Truck, MapPin, Calendar, Package, Home, ArrowLeft, User, FileText, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import TermsOfService from '../components/TermsOfService';
import AccessibleFormField from '../components/AccessibleFormField';
import AccessibleModal from '../components/AccessibleModal';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ItemSelector from '../components/ItemSelector';
import { HEAVY_FURNITURE_ITEMS, ELECTRICAL_ITEMS } from '../data/itemsCatalog';
import { MOVING_CATEGORY_OPTIONS, MOVING_CATEGORIES } from '../data/movingCategories';
import { BUILDING_TYPE_OPTIONS, ACCESS_PATH_OPTIONS_ARRAY } from '../data/addressConstants';
import useRecaptcha from '../hooks/useRecaptcha';
import logger from '../utils/logger';

const CustomerRequestPage = () => {
  const [step, setStep] = useState(1);
  const [requestId, setRequestId] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [originAddressData, setOriginAddressData] = useState(null);
  const [destinationAddressData, setDestinationAddressData] = useState(null);
  const [heavyFurniture, setHeavyFurniture] = useState([]);
  const [electricalItems, setElectricalItems] = useState([]);
  const [originAccessPath, setOriginAccessPath] = useState([]);
  const [destinationAccessPath, setDestinationAccessPath] = useState([]);
  const navigate = useNavigate();
  const { executeRecaptcha, isReady, error: recaptchaError } = useRecaptcha();

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      originAddress: '',
      destinationAddress: ''
    }
  });

  const STORAGE_KEY = 'customer_request_draft';

  // Clear building type and number of rooms when category changes away from "הובלת דירה"
  const currentCategory = watch('category');
  React.useEffect(() => {
    if (currentCategory !== MOVING_CATEGORIES.APARTMENT) {
      setValue('originBuildingType', '');
      setValue('originNumberOfRooms', '');
      setValue('destinationBuildingType', '');
      setValue('destinationNumberOfRooms', '');
    }
  }, [currentCategory, setValue]);

  // Load saved state on mount
  React.useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        
        // Restore component state
        if (parsedState.originAddressData) setOriginAddressData(parsedState.originAddressData);
        if (parsedState.destinationAddressData) setDestinationAddressData(parsedState.destinationAddressData);
        if (parsedState.heavyFurniture) setHeavyFurniture(parsedState.heavyFurniture);
        if (parsedState.electricalItems) setElectricalItems(parsedState.electricalItems);
        if (parsedState.originAccessPath) setOriginAccessPath(parsedState.originAccessPath);
        if (parsedState.destinationAccessPath) setDestinationAccessPath(parsedState.destinationAccessPath);
        
        // Restore form values
        if (parsedState.formValues) {
          reset(parsedState.formValues);
        }
      } catch (error) {
        console.error('Error parsing saved request state:', error);
        // If state is corrupted, clear it
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [reset]);

  // Watch all form values for saving
  const formValues = watch();

  // Save state on change with debounce
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Only save if there's actual data to save
      const hasData = Object.values(formValues).some(val => val) || 
                      heavyFurniture.length > 0 || 
                      electricalItems.length > 0 ||
                      originAccessPath.length > 0 ||
                      destinationAccessPath.length > 0;
      
      if (hasData) {
        const stateToSave = {
          formValues,
          originAddressData,
          destinationAddressData,
          heavyFurniture,
          electricalItems,
          originAccessPath,
          destinationAccessPath
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      }
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(timeoutId);
  }, [formValues, originAddressData, destinationAddressData, heavyFurniture, electricalItems, originAccessPath, destinationAccessPath]);


  const pendingFormDataRef = useRef(null);
  const verificationPhoneRef = useRef('');

  const onSubmit = async (data) => {
    if (!data.termsAccepted) {
      toast.error('יש לאשר את תנאי השימוש');
      return;
    }

    // Validate addresses are provided
    if (!data.originAddress || !data.originAddress.trim()) {
      toast.error('יש להזין כתובת מוצא');
      return;
    }
    if (!data.destinationAddress || !data.destinationAddress.trim()) {
      toast.error('יש להזין כתובת יעד');
      return;
    }

    // Show confirmation modal first
    pendingFormDataRef.current = data;
    setShowConfirmation(true);
    return;
  };

  const handleConfirmation = () => {
    setShowConfirmation(false);
    const data = pendingFormDataRef.current;
    if (data) {
      const phone = (data.customerPhone || '').trim();
      verificationPhoneRef.current = phone;
      setShowVerification(true);
    }
  };

  const performCreate = async (data) => {
    try {
      // Execute reCAPTCHA before request creation
      let recaptchaToken = null;
      if (isReady) {
        try {
          recaptchaToken = await executeRecaptcha('create_request');
        } catch (error) {
          logger.error('reCAPTCHA execution failed:', error);
          toast.error('שגיאה באימות אבטחה. אנא נסה שוב.');
          return;
        }
      } else if (recaptchaError) {
        logger.error('reCAPTCHA not ready:', recaptchaError);
        toast.error('שגיאה באימות אבטחה. אנא נסה שוב.');
        return;
      }

      // Only include building type and number of rooms if category is "הובלת דירה"
      const requestData = {
        ...data,
        requiresCrane: data.requiresCrane || false,
        heavyFurniture: heavyFurniture,
        electricalItems: electricalItems,
        originAccessPath: originAccessPath,
        destinationAccessPath: destinationAccessPath,
        // Include address validation data if available
        originLatitude: originAddressData?.latitude,
        originLongitude: originAddressData?.longitude,
        originPlaceId: originAddressData?.placeId,
        originAddressFormatted: originAddressData?.address,
        destinationLatitude: destinationAddressData?.latitude,
        destinationLongitude: destinationAddressData?.longitude,
        destinationPlaceId: destinationAddressData?.placeId,
        destinationAddressFormatted: destinationAddressData?.address,
        recaptchaToken,
      };

      // Remove building type and number of rooms if category is not "הובלת דירה"
      if (data.category !== MOVING_CATEGORIES.APARTMENT) {
        delete requestData.originBuildingType;
        delete requestData.originNumberOfRooms;
        delete requestData.destinationBuildingType;
        delete requestData.destinationNumberOfRooms;
      }

      const response = await axios.post(`${API_ENDPOINTS.REQUESTS}/create`, requestData);

      setRequestId(response.data.data.requestId);
      // Clear saved state on success
      localStorage.removeItem(STORAGE_KEY);
      setStep(2);
      toast.success('הבקשה נשלחה בהצלחה!');
    } catch (error) {
      logger.error('Error creating request:', error);
      toast.error(error.response?.data?.message || 'שגיאה בשליחת הבקשה');
    }
  };

  const verifyCode = async (phone) => {
    if (!verificationCode.trim()) {
      toast.error('יש להזין קוד אימות');
      return;
    }
    setIsVerifying(true);
    try {
      // Do not pre-verify on the client. Backend will verify during create.
      setShowVerification(false);
      const dataToSubmit = pendingFormDataRef.current;
      if (!dataToSubmit) {
        toast.error('שגיאה: נתוני הטופס לא נמצאו');
        return;
      }
      pendingFormDataRef.current = null;
      await performCreate({
        ...dataToSubmit,
        verificationCode
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בשליחת הבקשה');
    } finally {
      setIsVerifying(false);
    }
  };

  if (step === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 flex items-center justify-center">
        <div className="card max-w-md w-full text-center hover-lift">
          <div className="text-orange-500 mb-6">
            <Truck className="h-20 w-20 mx-auto" />
          </div>
          <h2 className="text-3xl font-bold text-brown-900 mb-6">
            הבקשה נשלחה בהצלחה!
          </h2>
          <p className="text-brown-700 mb-6 text-lg">
            מספר הבקשה שלך: <span className="font-bold text-orange-600">{requestId}</span>
          </p>
          <p className="text-brown-700 mb-8 text-lg">
            שמור את המספר הזה כדי לעקוב אחר הבקשה שלך
          </p>
          <div className="space-y-4">
            <button
              onClick={() => navigate(`/track/${requestId}`)}
              className="btn-primary w-full text-lg py-4"
            >
              עקוב אחר הבקשה
            </button>
            <button
              onClick={() => navigate('/')}
              className="btn-secondary w-full text-lg py-4"
            >
              חזור לדף הבית
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Skip link target */}
        <div id="main-content" className="sr-only" tabIndex="-1">תוכן ראשי</div>
        {/* Header */}
        <div id="navigation" className="mb-10" tabIndex="-1">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-orange-600 hover:text-orange-700 mb-6 text-lg font-semibold transition-colors duration-300"
          >
            <ArrowLeft className="h-5 w-5 ml-3" />
            חזור לדף הבית
          </button>
          <h1 className="text-4xl font-bold text-brown-900 mb-4">הזמנת הובלת דירה</h1>
          <p className="text-brown-700 mt-3 text-xl">מלא את הפרטים הבאים כדי לקבל הצעות מחיר ממובילים מקצועיים</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
          {/* Customer Information */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <User className="h-6 w-6 text-orange-600" />
              </div>
              פרטי הלקוח
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <AccessibleFormField
                label="שם מלא"
                id="customerName"
                error={errors.customerName?.message}
                required={true}
                helpText="הזן את שמך המלא כפי שמופיע בתעודת הזהות"
              >
                <input
                  type="text"
                  {...register('customerName', { required: 'שם הלקוח הוא שדה חובה' })}
                  maxLength={100}
                  className="input-field"
                  placeholder="הזן את שמך המלא"
                />
              </AccessibleFormField>

              <AccessibleFormField
                label="מספר טלפון"
                id="customerPhone"
                error={errors.customerPhone?.message}
                required={true}
                helpText="הזן מספר טלפון ישראלי בפורמט 05XXXXXXXX"
              >
                <input
                  type="tel"
                  {...register('customerPhone', { 
                    required: 'מספר הטלפון הוא שדה חובה',
                    pattern: {
                      value: /^05\d{8}$/,
                      message: 'מספר הטלפון חייב להיות בפורמט תקין (05XXXXXXXX)'
                    }
                  })}
                  maxLength={20}
                  className="input-field"
                  placeholder="05XXXXXXXX"
                />
              </AccessibleFormField>
            </div>

          </div>

          {/* Moving Category */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <Truck className="h-6 w-6 text-orange-600" />
              </div>
              סוג הובלה
            </h2>
            
            <AccessibleFormField
              label="בחר סוג הובלה"
              id="category"
              error={errors.category?.message}
              required={true}
              helpText="בחר את סוג ההובלה המתאים ביותר לבקשה שלך"
            >
              <select
                id="category"
                {...register('category', { 
                  required: 'יש לבחור סוג הובלה' 
                })}
                className="input-field"
              >
                <option value="">-- בחר סוג הובלה --</option>
                {MOVING_CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </AccessibleFormField>
          </div>

          {/* Origin Address */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <MapPin className="h-6 w-6 text-orange-600" />
              </div>
              כתובת המוצא
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 items-end">
              <div className="md:col-span-2">
                <AddressAutocomplete
                  id="originAddress"
                  label="כתובת המוצא"
                  value={watch('originAddress') || ''}
                  onChange={(e) => {
                    setValue('originAddress', e.target.value, { shouldValidate: true });
                  }}
                  onValidationChange={(validationData) => {
                    if (validationData.isValid) {
                      setOriginAddressData(validationData);
                      setValue('originAddress', validationData.address, { shouldValidate: true });
                    } else {
                      setOriginAddressData(null);
                    }
                  }}
                  error={errors.originAddress?.message}
                  required={true}
                  helpText="התחל להקליד כתובת ותקבל הצעות אוטומטיות"
                  placeholder="הזן את כתובת המוצא"
                />
              </div>

              <div className="form-field">
                <div className="h-5 mb-2" aria-hidden="true"></div>
                <label htmlFor="origin-floor" className="form-label">קומה *</label>
                <input
                  id="origin-floor"
                  type="number"
                  {...register('originFloor', { 
                    required: 'קומת המוצא היא שדה חובה',
                    min: { value: 0, message: 'קומה לא יכולה להיות שלילית' }
                  })}
                  className="input-field"
                  placeholder="0"
                  min="0"
                />
                {errors.originFloor && (
                  <p className="form-error">{errors.originFloor.message}</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register('originHasElevator')}
                  className="ml-2"
                />
                <span>יש מעלית בבניין</span>
              </label>
            </div>

            {watch('category') === MOVING_CATEGORIES.APARTMENT && (
              <div className="mt-8 grid md:grid-cols-2 gap-8">
                <AccessibleFormField
                  label="סוג הבית"
                  id="originBuildingType"
                  error={errors.originBuildingType?.message}
                  required={true}
                  helpText="בחר את סוג הבית בכתובת המוצא"
                >
                  <select
                    id="originBuildingType"
                    {...register('originBuildingType', { 
                      required: watch('category') === MOVING_CATEGORIES.APARTMENT ? 'סוג הבית במוצא הוא שדה חובה' : false
                    })}
                    className="input-field"
                  >
                    <option value="">-- בחר סוג בית --</option>
                    {BUILDING_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </AccessibleFormField>

                <AccessibleFormField
                  label="מספר חדרים"
                  id="originNumberOfRooms"
                  error={errors.originNumberOfRooms?.message}
                  required={true}
                  helpText="הזן את מספר החדרים בכתובת המוצא"
                >
                  <input
                    id="originNumberOfRooms"
                    type="number"
                    {...register('originNumberOfRooms', { 
                      required: watch('category') === MOVING_CATEGORIES.APARTMENT ? 'מספר החדרים במוצא הוא שדה חובה' : false,
                      min: { value: 0, message: 'מספר החדרים לא יכול להיות שלילי' },
                      max: { value: 20, message: 'מספר החדרים לא יכול לעלות על 20' }
                    })}
                    className="input-field"
                    placeholder="0"
                    min="0"
                    max="20"
                  />
                </AccessibleFormField>
              </div>
            )}

            <div className="mt-8">
              <div className="form-label mb-4 block">שביל גישה</div>
              <div className="space-y-2">
                {ACCESS_PATH_OPTIONS_ARRAY.map((option) => (
                  <label key={option.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={originAccessPath.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setOriginAccessPath([...originAccessPath, option.value]);
                        } else {
                          setOriginAccessPath(originAccessPath.filter(v => v !== option.value));
                        }
                      }}
                      className="ml-2"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Destination Address */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <Home className="h-6 w-6 text-orange-600" />
              </div>
              כתובת היעד
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 items-end">
              <div className="md:col-span-2">
                <AddressAutocomplete
                  id="destinationAddress"
                  label="כתובת היעד"
                  value={watch('destinationAddress') || ''}
                  onChange={(e) => {
                    setValue('destinationAddress', e.target.value, { shouldValidate: true });
                  }}
                  onValidationChange={(validationData) => {
                    if (validationData.isValid) {
                      setDestinationAddressData(validationData);
                      setValue('destinationAddress', validationData.address, { shouldValidate: true });
                    } else {
                      setDestinationAddressData(null);
                    }
                  }}
                  error={errors.destinationAddress?.message}
                  required={true}
                  helpText="התחל להקליד כתובת ותקבל הצעות אוטומטיות"
                  placeholder="הזן את כתובת היעד"
                />
              </div>

              <div className="form-field">
                <div className="h-5 mb-2" aria-hidden="true"></div>
                <label htmlFor="destination-floor" className="form-label">קומה *</label>
                <input
                  id="destination-floor"
                  type="number"
                  {...register('destinationFloor', { 
                    required: 'קומת היעד היא שדה חובה',
                    min: { value: 0, message: 'קומה לא יכולה להיות שלילית' }
                  })}
                  className="input-field"
                  placeholder="0"
                  min="0"
                />
                {errors.destinationFloor && (
                  <p className="form-error">{errors.destinationFloor.message}</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register('destinationHasElevator')}
                  className="ml-2"
                />
                <span>יש מעלית בבניין</span>
              </label>
            </div>

            {watch('category') === MOVING_CATEGORIES.APARTMENT && (
              <div className="mt-8 grid md:grid-cols-2 gap-8">
                <AccessibleFormField
                  label="סוג הבית"
                  id="destinationBuildingType"
                  error={errors.destinationBuildingType?.message}
                  required={true}
                  helpText="בחר את סוג הבית בכתובת היעד"
                >
                  <select
                    id="destinationBuildingType"
                    {...register('destinationBuildingType', { 
                      required: watch('category') === MOVING_CATEGORIES.APARTMENT ? 'סוג הבית ביעד הוא שדה חובה' : false
                    })}
                    className="input-field"
                  >
                    <option value="">-- בחר סוג בית --</option>
                    {BUILDING_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </AccessibleFormField>

                <AccessibleFormField
                  label="מספר חדרים"
                  id="destinationNumberOfRooms"
                  error={errors.destinationNumberOfRooms?.message}
                  required={true}
                  helpText="הזן את מספר החדרים בכתובת היעד"
                >
                  <input
                    id="destinationNumberOfRooms"
                    type="number"
                    {...register('destinationNumberOfRooms', { 
                      required: watch('category') === MOVING_CATEGORIES.APARTMENT ? 'מספר החדרים ביעד הוא שדה חובה' : false,
                      min: { value: 0, message: 'מספר החדרים לא יכול להיות שלילי' },
                      max: { value: 20, message: 'מספר החדרים לא יכול לעלות על 20' }
                    })}
                    className="input-field"
                    placeholder="0"
                    min="0"
                    max="20"
                  />
                </AccessibleFormField>
              </div>
            )}

            <div className="mt-8">
              <div className="form-label mb-4 block">שביל גישה</div>
              <div className="space-y-2">
                {ACCESS_PATH_OPTIONS_ARRAY.map((option) => (
                  <label key={option.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={destinationAccessPath.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setDestinationAccessPath([...destinationAccessPath, option.value]);
                        } else {
                          setDestinationAccessPath(destinationAccessPath.filter(v => v !== option.value));
                        }
                      }}
                      className="ml-2"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Moving Details */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <Calendar className="h-6 w-6 text-orange-600" />
              </div>
              פרטי ההובלה
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label htmlFor="preferred-date" className="form-label">תאריך ההובלה הרצוי *</label>
                <input
                  id="preferred-date"
                  type="date"
                  {...register('preferredDate', { required: 'תאריך ההובלה הוא שדה חובה' })}
                  className="input-field"
                  min={new Date().toISOString().split('T')[0]}
                />
                {errors.preferredDate && (
                  <p className="form-error">{errors.preferredDate.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="number-of-boxes" className="form-label flex items-center">
                  מספר ארגזים *
                  <div className="relative inline-block mr-2 group">
                    <HelpCircle 
                      className="h-4 w-4 text-gray-500 cursor-help" 
                      aria-label="מידע נוסף: סטייה של עד 15% יותר ארגזים מותרת"
                    />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-600 text-gray-50 text-sm rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible pointer-events-none z-10 shadow-md">
                      סטייה של עד 15% יותר ארגזים מותרת
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-600"></div>
                    </div>
                  </div>
                </label>
                <input
                  id="number-of-boxes"
                  type="number"
                  {...register('numberOfBoxes', { 
                    required: 'מספר הארגזים הוא שדה חובה',
                    min: { value: 0, message: 'מספר הארגזים לא יכול להיות שלילי' }
                  })}
                  className="input-field"
                  placeholder="0"
                  min="0"
                  title="סטייה של עד 15% יותר ארגזים מותרת"
                />
                {errors.numberOfBoxes && (
                  <p className="form-error">{errors.numberOfBoxes.message}</p>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mt-6">
              <div>
                <label htmlFor="number-of-bags" className="form-label">מספר באלות *</label>
                <input
                  id="number-of-bags"
                  type="number"
                  {...register('numberOfBags', { 
                    required: 'מספר הבאלות הוא שדה חובה',
                    min: { value: 0, message: 'מספר הבאלות לא יכול להיות שלילי' }
                  })}
                  className="input-field"
                  placeholder="0"
                  min="0"
                />
                {errors.numberOfBags && (
                  <p className="form-error">{errors.numberOfBags.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="number-of-clothing-bags" className="form-label">מספר שקיות בגדים *</label>
                <input
                  id="number-of-clothing-bags"
                  type="number"
                  {...register('numberOfClothingBags', { 
                    required: 'מספר שקיות הבגדים הוא שדה חובה',
                    min: { value: 0, message: 'מספר שקיות הבגדים לא יכול להיות שלילי' }
                  })}
                  className="input-field"
                  placeholder="0"
                  min="0"
                />
                {errors.numberOfClothingBags && (
                  <p className="form-error">{errors.numberOfClothingBags.message}</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register('requiresCrane')}
                  className="ml-2"
                />
                <span>נדרש מנוף</span>
              </label>
            </div>
          </div>

          {/* Items */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <Package className="h-6 w-6 text-orange-600" />
              </div>
              פריטים להובלה
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <ItemSelector
                id="heavy-furniture"
                items={HEAVY_FURNITURE_ITEMS}
                selectedItems={heavyFurniture}
                onAdd={(item) => {
                  const newItem = { ...item };
                  // Preserve catalog id for predefined items, generate id for custom items
                  if (!newItem.id && !newItem.isCustom) {
                    const catalogItem = HEAVY_FURNITURE_ITEMS.find(i => i.name === item.name);
                    if (catalogItem) {
                      newItem.id = catalogItem.id;
                    }
                  }
                  if (!newItem.id) {
                    newItem.id = `hf-${Date.now()}-${Math.random()}`;
                  }
                  setHeavyFurniture([...heavyFurniture, newItem]);
                }}
                onRemove={(index) => {
                  setHeavyFurniture(heavyFurniture.filter((_, i) => i !== index));
                }}
                onUpdate={(index, item) => {
                  const updated = [...heavyFurniture];
                  updated[index] = { ...item, id: updated[index].id || `hf-${Date.now()}-${Math.random()}` };
                  setHeavyFurniture(updated);
                }}
                categoryLabel="רהיטים כבדים"
                helpText="בחר רהיטים מהרשימה או הוסף פריטים מותאמים"
              />

              <ItemSelector
                id="electrical-items"
                items={ELECTRICAL_ITEMS}
                selectedItems={electricalItems}
                onAdd={(item) => {
                  const newItem = { ...item };
                  // Preserve catalog id for predefined items, generate id for custom items
                  if (!newItem.id && !newItem.isCustom) {
                    const catalogItem = ELECTRICAL_ITEMS.find(i => i.name === item.name);
                    if (catalogItem) {
                      newItem.id = catalogItem.id;
                    }
                  }
                  if (!newItem.id) {
                    newItem.id = `ei-${Date.now()}-${Math.random()}`;
                  }
                  setElectricalItems([...electricalItems, newItem]);
                }}
                onRemove={(index) => {
                  setElectricalItems(electricalItems.filter((_, i) => i !== index));
                }}
                onUpdate={(index, item) => {
                  const updated = [...electricalItems];
                  updated[index] = { ...item, id: updated[index].id || `ei-${Date.now()}-${Math.random()}` };
                  setElectricalItems(updated);
                }}
                categoryLabel="מוצרי חשמל"
                helpText="בחר מוצרי חשמל מהרשימה או הוסף פריטים מותאמים"
              />
            </div>

            <div className="mt-8">
              <label htmlFor="additional-notes" className="form-label">הערות נוספות</label>
              <textarea
                id="additional-notes"
                {...register('additionalNotes')}
                maxLength={1000}
                className="input-field"
                rows="3"
                placeholder="הערות נוספות או דרישות מיוחדות..."
              />
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <FileText className="h-6 w-6 text-orange-600" />
              </div>
              תנאי שימוש
            </h2>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-3 space-x-reverse">
                <input
                  type="checkbox"
                  {...register('termsAccepted', { 
                    required: 'יש לאשר את תנאי השימוש' 
                  })}
                  className="mt-1 ml-2"
                  id="termsAccepted"
                />
                <div className="flex-1">
                  <label htmlFor="termsAccepted" className="text-lg text-brown-700">
                    קראתי ואני מסכים ל{' '}
                    <button
                      type="button"
                      onClick={() => setShowTerms(true)}
                      className="text-orange-600 hover:text-orange-700 underline font-medium"
                    >
                      תנאי השימוש
                    </button>
                    {' '}של החברה
                  </label>
                  {errors.termsAccepted && (
                    <p className="form-error mt-2">{errors.termsAccepted.message}</p>
                  )}
                </div>
              </div>
              
              <p className="text-lg text-brown-700">
                על ידי סימון תיבת הסימון, אתה מאשר שקראת והבנת את תנאי השימוש ומסכים להם.
                הסכמה זו מהווה תנאי חובה להגשת הבקשה.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary text-lg px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'שולח...' : 'שלח בקשה'}
            </button>
          </div>
        </form>
      </div>

      {/* Terms of Service Modal */}
      <TermsOfService 
        isOpen={showTerms} 
        onClose={() => setShowTerms(false)} 
      />

      {/* Confirmation Modal */}
      <AccessibleModal
        isOpen={showConfirmation}
        onClose={() => { setShowConfirmation(false); pendingFormDataRef.current = null; }}
        title="אישור הגשת הבקשה"
      >
        <div className="text-brown-700 mb-8 text-lg leading-relaxed text-center">
          <p className="mb-4">
            אני מצהיר שפרטי הבקשה להובלה נכונים ומדוייקים. אני מבין ומקבל שבמידה ותמצא אי תאימות בין פרטי ההובלה בבקשה שהגשתי לפרטים במועד ההובלה בפועל המוביל רשאי לשנות את המחיר ואף לבטל את ההובלה באופן חד צדדי.
          </p>
        </div>
        <div className="flex space-x-4 space-x-reverse">
          <button
            onClick={() => { setShowConfirmation(false); pendingFormDataRef.current = null; }}
            className="btn-secondary flex-1 text-lg py-3"
          >
            ביטול
          </button>
          <button
            onClick={handleConfirmation}
            className="btn-primary flex-1 text-lg py-3"
          >
            אני מסכים - המשך
          </button>
        </div>
      </AccessibleModal>

    {/* Phone Verification Modal */}
    <AccessibleModal
      isOpen={showVerification}
      onClose={() => { setShowVerification(false); pendingFormDataRef.current = null; }}
      title="אימות מספר טלפון"
    >
      <p className="text-brown-700 mb-6 text-center">
        נשלח קוד אימות למספר הטלפון שהוזן בטופס
      </p>
      <div className="space-y-4">
        <div className="flex space-x-3 space-x-reverse">
          <button
            onClick={async () => {
              try {
                const currentPhone = verificationPhoneRef.current || getValues('customerPhone');
                await axios.post(`${API_ENDPOINTS.SMS}/send-verification`, {
                  phone: currentPhone
                });
                toast.success('נשלח קוד אימות לטלפון שלך');
              } catch (error) {
                toast.error(error.response?.data?.message || 'שגיאה בשליחת קוד האימות');
              }
            }}
            className="btn-secondary flex-1"
          >
            שלח קוד אימות
          </button>
        </div>
        <div>
          <label htmlFor="verification-code-request" className="sr-only">קוד אימות</label>
          <input
            id="verification-code-request"
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="הזן קוד אימות"
            className="input-field text-center text-2xl tracking-widest"
            maxLength={6}
            aria-describedby="verification-help-request"
          />
          <div id="verification-help-request" className="text-sm text-gray-600 mt-1 text-center">
            הזן את הקוד בן 6 הספרות שנשלח לטלפון שלך
          </div>
        </div>
        <div className="flex space-x-3 space-x-reverse">
          <button
            onClick={() => { setShowVerification(false); pendingFormDataRef.current = null; }}
            className="btn-secondary flex-1"
          >
            ביטול
          </button>
          <button
            onClick={() => verifyCode(verificationPhoneRef.current || getValues('customerPhone'))}
            disabled={isVerifying}
            className="btn-primary flex-1"
          >
            {isVerifying ? 'מאמת...' : 'אמת והגש'}
          </button>
        </div>
      </div>
    </AccessibleModal>
    </div>
  );
};

export default CustomerRequestPage;
